/*
 *
 * Copyright (c) 2020 TenderHub LLC. All rights reserved
 *
 * This software is the confidential and proprietary information of
 * TenderHub LLC ("Confidential Information"). You shall not disclose
 * such Confidential Information.
 *
 */

import { AuthenticationScheme, HttpHeaderKey, IEnvironment, LocaleId } from '@site-shared/common';
import { EPragmaEndpoints, RegistrationEndpoints } from '@site-shared/data';

export const environmentBase: IEnvironment = {
	application: 'CONSOLE',
	production: false,
	apiUrl: '/api',
	locale: LocaleId.RU,
	localeConfig: {
		[LocaleId.RU]: {
			delimiters: {
				thousands: ' ',
				decimal: ',',
			},
			abbreviations: {
				thousand: 'тыс',
				million: 'млн',
				billion: 'млрд',
				trillion: 'трлн',
			},
			currency: {
				symbol: 'Р',
			},
		},
		[LocaleId.EN]: {
			delimiters: {
				thousands: ' ',
				decimal: ',',
			},
			abbreviations: {
				thousand: 'k',
				million: 'm',
				billion: 'b',
				trillion: 't',
			},
			currency: {
				symbol: '€',
			},
		},
		[LocaleId.AR]: {
			delimiters: {
				thousands: ' ',
				decimal: ',',
			},
			abbreviations: {
				thousand: 'k',
				million: 'm',
				billion: 'b',
				trillion: 't',
			},
			currency: {
				symbol: '€',
			},
		},
	},
	authorizationMethod: AuthenticationScheme.Bearer,
	authorizationKey: HttpHeaderKey.Authorization,
	accessTokenKey: 'access',
	loginUrl: '/auth/login',
	logoutUrl: '/403',
	endpoints: {
		checkPassword: 'auth/login',
		changePassword: 'users/password',
		newPassword: 'users/password',
		resetPassword: 'users/password/reset',
		resetSendPassword: 'users/password/reset/send',
		checkDisclaimers: 'users/{0}/terms-of-service',
		verifyEmailAgain: 'users/{0}/verifications/email',
		verifyEmail: 'users/email',
		acceptDisclaimers: 'users/{0}/terms-of-service/acceptance',
		generateMultifactor: 'users/multi-factor/generation',
		multifactor: 'users/multi-factor',
		getLookupValues: 'core/lookups/{0}/values',
		inviteUsers: 'users/invitations',
		acceptInvitation: 'users/invitations/accept',
		getTerm: 'terms/{0}',
		resolveIssue: 'issues/{0}/resolve',
		feedback: 'feedback',
		'api/user/PATCH': 'users/{0}',
		'api/user/GET': 'users/{0}',
		'api/user/SEARCH': 'users/search',
		'api/userimage/POST': 'users/{0}/image',
		'api/userimage/GET': 'users/{0}/image',
		'api/usertoken/GET': 'users/{0}/tokens',
		'api/usertoken/lock': 'users/{0}/tokens/lock',
		'api/usertoken/unlock': 'users/{0}/tokens/unlock',
		'api/usertoken/POST': 'users/{0}/tokens',
		'api/usertoken/SEARCH': 'users/tokens/search',
		'api/usertoken/DELETE': 'users/{0}/tokens',
		'api/usersession/GET': 'users/{0}/sessions',
		'api/usersession/DELETE': 'users/{0}/sessions',
		'api/userevent/SEARCH': 'audits/users/search',
		'api/userterm/SEARCH': 'users/{0}/terms-of-service/search',
		'api/userrole/GET': 'users/{0}/roles',
		'api/userrole/PATCH': 'users/{0}/roles',
		'api/assigneduser/ASSIGNED_USERS': 'roles/{0}/users',
		'api/role/GET': 'roles/{0}',
		'api/card/SEARCH': 'ui/cards/search',
		'api/wfrole/SEARCH': 'actors/search',
		'api/wfrole/GET': 'actors/{0}',
		'api/wfrole/ATTACH': 'actors/{0}/persons/{1}',
		'api/wfrole/DETACH': 'actors/{0}/persons/{1}',
		'api/ci/contracts/current': 'ci/contracts/current',
		'api/issue/SEARCH': 'issues/search',
		'api/issue/GET': 'issues/{0}',
		'api/action/GET': 'core/actions/{0}',
		'api/action/GET_DOMAIN': 'core/domains/{0}/actions',
		'api/action/EXECUTE': 'core/actions/{0}/execute',
		'api/query/GET_FOR_DOMAIN': 'core/domains/{0}/queries',
		'api/query/GET_BY_NAME': 'core/queries/{0}',
		'api/query/EXECUTE': 'core/queries/{0}/execute',
		'api/document/ADD': 'cm/docs/{0}/files',
		'api/document/GET_ZIP': 'cm/docs/{0}/files',
		'api/document/files/GET': 'cm/docs/{0}/files/{1}',
		'api/document/files/DELETE': 'cm/docs/{0}/files/{1}',
		[EPragmaEndpoints.GET_DATA]: 'ui/pragmas/{0}/values',
		[EPragmaEndpoints.GET_DATA_WL]: 'wl/pragmas/{0}/values',
		[RegistrationEndpoints.SIGNON]: 'wl/signon',
		[RegistrationEndpoints.ACCEPT]: 'wl/signon/accept',
		[RegistrationEndpoints.META]: 'wl/actions/{0}',
		[RegistrationEndpoints.SUGGESTION]: 'wl/suggestions/{0}/query',
	},
	configUrl: 'config.json',
	disclaimersUrl: '/auth/terms',
	recoveryTokenKey: 't',
	recaptchaSiteKey: '6LfQP-MZAAAAAIyBfEK1uUNIsOPwKcZTIt0bcebj',
	rootTitle: 'TenderHub',
};
